import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Save, Pencil, Upload, X } from "lucide-react";

// ✅ Use environment variable for API base
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectHeroSectionForm = ({
  apiBase = `${API_BASE_URL}/projects`, // ✅ Default to env-based API path
  token = localStorage.getItem("token"),
}) => {
  const [heroData, setHeroData] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    image: "",
  });
  const [loading, setLoading] = useState(false);

  // ===========================================
  // Fetch Hero Data (GET)
  // ===========================================
  const fetchProjectHero = async () => {
    try {
      // ✅ Updated: use env-based API path
      const res = await axios.get(`${apiBase}/admin/hero`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(res.data)) setHeroData(res.data);
      else setHeroData([]);
    } catch (err) {
      console.error("Error fetching project hero:", err);
      alert("Failed to load project hero data.");
    }
  };

  useEffect(() => {
    fetchProjectHero();
  }, []);

  // ===========================================
  // Handle Form Change
  // ===========================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ===========================================
  // Handle Image Upload (Base64)
  // ===========================================
const handleImageUpload = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const validTypes = ["image/jpeg", "image/png", "image/webp"];
  if (!validTypes.includes(file.type)) {
    alert("Please upload a valid image (JPG, PNG, or WEBP).");
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    alert("Image must be under 2MB.");
    return;
  }

  // ✅ Upload to S3
  const formDataUpload = new FormData();
  formDataUpload.append("media", file);

  axios
    .post(`${API_BASE_URL}/admin/upload-media`, formDataUpload, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => {
      console.log("upload response", res.data);

      const uploadedUrl = res.data.data.files?.[0]?.url;
      console.log("uploadedUrl", uploadedUrl);

      if (uploadedUrl) {
        setFormData((prev) => ({ ...prev, image: uploadedUrl }));
      }
    })
    .catch((err) => {
      console.error("upload-media error", err);
      alert("Failed to upload image. Please try again.");
    });
};

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  // ===========================================
  // Handle Edit
  // ===========================================
  const handleEdit = (hero) => {
    setEditingId(hero.project_hero_id);
    setFormData({
      title: hero.title || "",
      subtitle: hero.subtitle || "",
      image: hero.image || "",
    });
    setFormOpen(true);
  };

  // ===========================================
  // Handle Save / Update
  // ===========================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.subtitle.trim()) {
      alert("Title and Subtitle are required.");
      return;
    }

    try {
      setLoading(true);

      // ✅ Updated: use env-based API path
      await axios.post(
        `${apiBase}/admin/hero/save`,
        {
          project_hero_id: editingId || 0,
          title: formData.title,
          subtitle: formData.subtitle,
          image: formData.image,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(
        editingId
          ? "Project Hero updated successfully"
          : "Project Hero added successfully"
      );
      setFormOpen(false);
      setEditingId(null);
      setFormData({ title: "", subtitle: "", image: "" });
      fetchProjectHero();
    } catch (err) {
      console.error("Error saving project hero:", err);
      alert("Failed to save project hero.");
    } finally {
      setLoading(false);
    }
  };

  // ===========================================
  // Render
  // ===========================================
  return (
    <section className="bg-white rounded-xl shadow p-8 mb-10">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">
          Project Hero Section
        </h2>
        <button
          onClick={() => {
            if (formOpen) setEditingId(null);
            setFormOpen((p) => !p);
          }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
        >
          <Plus size={18} />
          {formOpen ? "Close" : "Add / Edit Hero"}
        </button>
      </div>

      {/* Display Current Hero */}
      {heroData.length > 0 ? (
        <div className="overflow-x-auto mb-8">
          <table className="min-w-full border border-gray-200 text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-6 py-3 text-left font-semibold">Title</th>
                <th className="px-6 py-3 text-left font-semibold">Subtitle</th>
                <th className="px-6 py-3 text-left font-semibold">Image</th>
                <th className="px-6 py-3 text-center font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {heroData.map((hero) => (
                <tr
                  key={hero.project_hero_id}
                  className="border-t hover:bg-gray-50 transition"
                >
                  <td className="px-6 py-3">{hero.title}</td>
                  <td className="px-6 py-3">{hero.subtitle}</td>
                  <td className="px-6 py-3">
                    {hero.image ? (
                      <img
                        src={hero.image}
                        alt="Preview"
                        className="h-12 w-20 object-cover rounded"
                      />
                    ) : (
                      <span className="text-gray-400 italic">No image</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-center">
                    <button
                      onClick={() => handleEdit(hero)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Pencil size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 italic mb-8">
          No Project Hero data available.
        </p>
      )}

      {/* Form */}
      {formOpen && (
        <form onSubmit={handleSubmit} className="border-t pt-6 mt-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            {editingId ? "Edit Project Hero" : "Add Project Hero"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <input
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                required
                className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
              />
            </div>

            {/* Image Upload */}
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Hero Image</label>
              {formData.image ? (
                <div className="relative inline-block">
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="w-full max-w-md h-64 object-cover rounded-lg border"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    id="project-hero-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <label
                    htmlFor="project-hero-upload"
                    className="cursor-pointer flex flex-col items-center gap-3"
                  >
                    <Upload size={40} className="text-gray-400" />
                    <span className="text-gray-600">Click to upload image</span>
                     <span className="text-xs text-gray-400">
                        Recommended image size: 1920×1080 px (16:9), JPG/PNG, upto 500 KB
                      </span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg"
            >
              <Save size={18} />
              {loading
                ? "Saving..."
                : editingId
                ? "Update Project Hero"
                : "Save Project Hero"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default ProjectHeroSectionForm;
